import { HowToPlanning } from 'src/entities/PlanningHistory.entity';
import { migrateRelaxedRules } from 'src/utils/relaxedRule.util';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import {
  DEFAULT_PLANNING_SETTINGS,
  EXPAND_SETTING_KEYS,
  migrateDispatchModel,
  PlanningDialogSettings,
  VehicleSizePreference,
} from './dispatchModel';

/** 送る値の snapshot。自動配車の履歴(planningConditions)の元データも兼ねる(PlansContext / TrunkTransportation が読む) */
export const PLANNING_SETTINGS_STORAGE_KEY = 'PlanningPresenterSettings';
/**
 * 画面で選んだモデルと、SHIKI / SAHAI では送らない LOG 3.0 の値。V3 の復元だけが読む。
 * Why not 既存キーに入れる: 既存キーは中身がそのまま履歴の planningConditions として API に送られる。
 */
export const STANDARD_SETTINGS_STORAGE_KEY = 'PlanningPresenterStandardSettings';

const STANDARD_SNAPSHOT_KEYS = [
  'dispatchModel',
  'howToPlanning',
  'vehicleSizePreference',
  'includeFutureDeliveries',
  'dontExchangePreroute',
  'activateIncrementalMaxNumPistons',
  'concurrentAllOrNothing',
] as const satisfies readonly (keyof PlanningDialogSettings)[];

export type StandardSettingsSnapshot = Pick<PlanningDialogSettings, typeof STANDARD_SNAPSHOT_KEYS[number]>;

export function pickStandardSettings(settings: PlanningDialogSettings): StandardSettingsSnapshot {
  return Object.fromEntries(STANDARD_SNAPSHOT_KEYS.map((key) => [key, settings[key]])) as StandardSettingsSnapshot;
}

export type RestoredPlanningDialog = {
  settings: PlanningDialogSettings;
  /** 保存されていなければ undefined（親の state を触らない） */
  selectedRelaxedRules: RelaxedRuleVo[] | undefined;
};

type Saved = Record<string, unknown>;

const parse = (json: string | null): Saved | null => {
  if (!json) return null;
  try {
    const value: unknown = JSON.parse(json);
    return value && typeof value === 'object' ? (value as Saved) : null;
  } catch {
    return null;
  }
};

const BOOLEAN_KEYS = [
  'availabilityOfExpressway',
  'includeFutureDeliveries',
  'dontExchangePreroute',
  'activateIncrementalMaxNumPistons',
  'concurrentAllOrNothing',
] as const satisfies readonly (keyof PlanningDialogSettings)[];

const isHowToPlanning = (v: unknown): v is HowToPlanning => v === '最適化' || v === '平準化';
const isVehicleSizePreference = (v: unknown): v is VehicleSizePreference => v === 'none' || v === 'small' || v === 'large';

// How: 型の合う値だけを拾う。localStorage は別画面(V2)や過去のバージョンも書くため形を信用しない
const applySaved = (base: PlanningDialogSettings, saved: Saved, keys: readonly (keyof PlanningDialogSettings)[]) => {
  const next = { ...base };
  if (keys.includes('howToPlanning') && isHowToPlanning(saved.howToPlanning)) next.howToPlanning = saved.howToPlanning;
  if (keys.includes('vehicleSizePreference') && isVehicleSizePreference(saved.vehicleSizePreference)) {
    next.vehicleSizePreference = saved.vehicleSizePreference;
  }
  BOOLEAN_KEYS.forEach((key) => {
    if (keys.includes(key) && typeof saved[key] === 'boolean') next[key] = saved[key];
  });
  EXPAND_SETTING_KEYS.forEach((key) => {
    if (keys.includes(key) && typeof saved[key] === 'number') next[key] = saved[key];
  });
  return next;
};

const ALL_KEYS = Object.keys(DEFAULT_PLANNING_SETTINGS) as (keyof PlanningDialogSettings)[];

/**
 * localStorage の 2 キーから画面の設定を組み立てる。
 * 1. 既存キーの値を入れ、モデルは旧値を読み替える
 * 2. 選んだモデルは新しいキーを優先する（既存キーは実際に動いたモデルで、ライセンス取得前は standard になる）
 * 3. 既存キーが SHIKI / SAHAI で保存されていたときだけ、LOG 3.0 の値を新しいキーから戻す
 *    Why not 無条件に戻す: V2（TrunkTransportation）は既存キーだけを dispatchModel 無しで書くので、
 *    無条件だと V2 で変えた値を V3 側の古い値に戻してしまう
 * 4. 自動分割は戻さない（配車結果を実際に変えるので毎回 OFF から始める。V2 も OFF 固定）
 */
export function restorePlanningDialog(savedJson: string | null, standardJson: string | null): RestoredPlanningDialog {
  const saved = parse(savedJson);
  const standard = parse(standardJson);

  let settings = saved ? applySaved(DEFAULT_PLANNING_SETTINGS, saved, ALL_KEYS) : { ...DEFAULT_PLANNING_SETTINGS };
  const savedModel = migrateDispatchModel(saved?.dispatchModel, saved?.dispatchScenario);
  settings.dispatchModel = standard ? migrateDispatchModel(standard.dispatchModel, undefined) : savedModel;

  if (standard && savedModel !== 'standard') {
    settings = applySaved(settings, standard, STANDARD_SNAPSHOT_KEYS);
  }
  settings.autoSplitOrders = false;

  const rules = saved?.selectedRelaxedRules;
  return {
    settings,
    selectedRelaxedRules: Array.isArray(rules) ? migrateRelaxedRules(rules as RelaxedRuleVo[]) : undefined,
  };
}
